// What an invoice says, as data.
//
// Deliberately free of jsPDF, Firebase and the DOM: importing this must not drag
// in vendor.js, or the pure logic can only be tested inside a browser
// environment. Rendering and data loading live in invoicePdf.js.

import { parseLocalDate } from './dates.js';
import { RevenueService } from './revenue.js';

const NET_DAYS = 7; // matches the app's own "overdue after 7 days" rule

function fmtLong(date) {
  return date
    ? date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;
}

/**
 * Decide what the invoice says. Pure: no jsPDF, no network, no clock beyond the
 * `today` you pass in, so it can be tested.
 */
export function buildInvoiceModel({ job = {}, invoice = {}, vendor = {}, today = new Date() }) {
  const amount = RevenueService.parseJobAmount(
    invoice.amount ?? invoice.total ?? job.quotedPrice
  );

  // "Date: N/A" used to reach customers whenever an invoice had no dateCreated -
  // which is every not-yet-saved invoice, and seven existing records besides.
  // An invoice always has a date: the day it was raised.
  const issued =
    parseLocalDate(invoice.dateCreated) || parseLocalDate(invoice.sentDate) || today;

  const due = new Date(issued.getTime());
  due.setDate(due.getDate() + NET_DAYS);

  const paidOn = parseLocalDate(invoice.datePaid);
  const status = String(invoice.status || '').toLowerCase();

  return {
    invoiceNumber: invoice.invoiceNumber || null,
    issuedLabel: fmtLong(issued),
    dueLabel: fmtLong(due),
    paidLabel: fmtLong(paidOn),
    isPaid: status === RevenueService.INVOICE_STATUSES.PAID,
    terms: `Net ${NET_DAYS}`,

    vendor: {
      name: vendor.companyName || job.vendor || 'Your Company',
      email: vendor.email || '',
      website: vendor.websiteURL || '',
      // Printed on the invoice so the ask reaches customers who are billed
      // locally rather than through Stripe. Validated where it is entered and
      // again server-side; here it is passed through untouched.
      reviewUrl: vendor.reviewUrl || '',
    },

    customer: {
      name: job.customerName || invoice.customerName || '',
      email: job.customerEmail || invoice.customerEmail || '',
      phone: job.customerPhone || '',
    },

    service: {
      type: job.serviceType || invoice.serviceType || 'Service',
      location: job.serviceLocation || invoice.serviceLocation || '',
      // The date the work happened, which is not the date it was invoiced.
      performedLabel: fmtLong(parseLocalDate(job.scheduledDate)),
      notes: (job.comments || '').trim(),
    },

    amount,
    amountLabel: RevenueService.formatCurrency(amount),
    fileName: `invoice-${invoice.invoiceNumber || job.id || 'draft'}.pdf`,
  };
}
